import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  ReactElement,
} from 'react';
import { flushSync } from 'react-dom';
import { Box } from '@mui/material';
import { TextLabelState } from '../../../api/Requests';
import { TEXT_FONT_FAMILY, TEXT_LINE_HEIGHT } from '../../../utils/fitFontSize';
import { DEFAULT_LABEL_FONT } from '../../../utils/stickyResize';
import {
  POINTER_DRAG_THRESHOLD_PX,
  displayZIndex,
  MAX_LABEL_CONTENT_LENGTH,
  clampTextLength,
} from '../../../utils/canvasUtils';
import {
  handleContentEditableBeforeInput,
  truncateContentEditableIfNeeded,
} from '../../../utils/contentEditableMaxLength';
import ResizeCornerHandles, { ItemResizeHandleProps } from './ResizeCornerHandles';
import { applyLiveItemPosition, clearLiveItemPosition } from './liveDragPosition';
import ItemOccupancyOverlay from './ItemOccupancyOverlay';

// Whiteboard marker palette for light canvas (dark ink on light background).
// Order matches keyboard shortcuts 1–6 in CanvasSurface.
export const LABEL_COLORS_LIGHT = [
  '#1a1a1a', // 1 Black  (default)
  '#1565c0', // 2 Blue
  '#c62828', // 3 Red
  '#2e7d32', // 4 Green
  '#e65100', // 5 Orange
  '#6a1b9a', // 6 Purple
];

// Chalk palette for dark canvas (light tones readable on #1e1e1e background).
export const LABEL_COLORS_DARK = [
  '#e0e0e0', // 1 White chalk  (default)
  '#64b5f6', // 2 Light blue
  '#ef9a9a', // 3 Light red
  '#a5d6a7', // 4 Light green
  '#ffcc80', // 5 Light orange
  '#ce93d8', // 6 Light purple
];

// Convenience alias — used by components that don't need to distinguish light/dark.
export const LABEL_COLORS = LABEL_COLORS_LIGHT;

/** Must match the selection `outlineOffset` on the label box. */
export const LABEL_OUTLINE_OFFSET_PX = 4;

/** Horizontal padding on the label root — keep in sync with the root `sx.padding`. */
export const LABEL_PADDING_X_PX = 8;
/** Vertical padding on the label root — keep in sync with the root `sx.padding`. */
export const LABEL_PADDING_Y_PX = 4;
/** Inner editor min-width so an empty title still has a layout box. */
export const LABEL_INNER_MIN_WIDTH_PX = 1;

/** Last-resort size for a title with text whose DOM node is not mounted yet. */
const UNMEASURED_LABEL_WITH_TEXT = { w: 200, h: 56 };

/** Board-space box of a freshly created (empty) title — matches TextLabel's CSS. */
export function emptyLabelBoxSize(fontSize: number = DEFAULT_LABEL_FONT): { w: number; h: number } {
  return {
    w: LABEL_PADDING_X_PX * 2 + LABEL_INNER_MIN_WIDTH_PX,
    h: LABEL_PADDING_Y_PX * 2 + fontSize * TEXT_LINE_HEIGHT,
  };
}

/**
 * Size used to centre the color strip under a title.
 * Empty titles (the create-then-pick-a-color flow) always use the CSS box, not
 * the live DOM: the node is missing on the first parent render (ref not set
 * yet), and focusing contenteditable can widen it a frame later. Measuring
 * either of those used to place the strip ~100px too far right, so it jumped
 * left once the real fit-content width landed.
 */
export function labelColorStripAnchorSize(
  label: Pick<TextLabelState, 'content' | 'fontSize'>,
  el: { clientWidth: number; clientHeight: number } | null | undefined,
): { w: number; h: number } {
  if (!(label.content ?? '').trim()) return emptyLabelBoxSize(label.fontSize ?? DEFAULT_LABEL_FONT);
  if (el) return { w: el.clientWidth, h: el.clientHeight };
  return UNMEASURED_LABEL_WITH_TEXT;
}

export interface TextLabelProps extends ItemResizeHandleProps {
  label: TextLabelState;
  isSelected: boolean;
  isMultiSelected?: boolean;
  onSelect: (id: string) => void;
  onChange: (diff: Partial<TextLabelState> & { id: string }) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  onDelete: (id: string) => void;
  scale: number;
  isPanMode?: boolean;
  onEditingChange?: (id: string | null) => void;
  onDraggingChange?: (id: string | null) => void;
  /** Called whenever the live edit text changes — true if the editor has any content */
  onHasTextChange?: (id: string, hasText: boolean) => void;
  onMultiDragMove?: (id: string, dx: number, dy: number) => void;
  onMultiDragEnd?: (id: string, dx: number, dy: number) => void;
  shouldStartEdit?: boolean;
  /** Accent color for the selection outline — derived from theme.canvas.selectionColor in CanvasSurface. */
  selectionColor?: string;
  /**
   * Mode-appropriate label color palette resolved by CanvasSurface (LABEL_COLORS_LIGHT or
   * LABEL_COLORS_DARK). Used to resolve label.colorSlot to the correct hex at render time.
   */
  labelColors?: readonly string[];
  occupiedByName?: string;
  /**
   * Bumped by CanvasSurface when occupancy is denied. Aborts in-flight edit/drag
   * even when the overlay never appears (hub error, deny without another holder).
   */
  occupancyAbortEpoch?: number;
}

const TextLabel = React.forwardRef<HTMLDivElement, TextLabelProps>(
  function TextLabel(
    {
      label,
      isSelected,
      isMultiSelected = false,
      onSelect,
      onChange,
      onDragEnd,
      onDelete,
      scale,
      isPanMode = false,
      onEditingChange,
      onDraggingChange,
      onHasTextChange,
      onMultiDragMove,
      onMultiDragEnd,
      shouldStartEdit = false,
      selectionColor = '#2b6be6',
      labelColors = LABEL_COLORS_LIGHT,
      occupiedByName,
      occupancyAbortEpoch = 0,
      showResizeHandles = false,
      onResizePointerDown,
      onResizePointerMove,
      onResizePointerUp,
      onResizePointerCancel,
      onResizeLostPointerCapture,
    },
    ref,
  ): ReactElement {
    // Resolve display color from slot index so it adapts to the active theme mode.
    const resolvedColor = labelColors[label.colorSlot] ?? labelColors[0];
    const editRef      = useRef<HTMLDivElement>(null);
    const nodeRef      = useRef<HTMLDivElement | null>(null);
    const dragStartPos = useRef<{ mx: number; my: number; bx: number; by: number } | null>(null);
    const livePosRef   = useRef<{ x: number; y: number } | null>(null);
    const suppressPersistRef = useRef(false);
    const occupancyAbortEpochRef = useRef(occupancyAbortEpoch);
    const [isDragging, setIsDragging] = useState(false);
    const [isMoving,   setIsMoving]   = useState(false);
    const [isEditing,  setIsEditing]  = useState(false);

    const setRootRef = useCallback((el: HTMLDivElement | null) => {
      nodeRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    }, [ref]);

    // Cap content before display/edit so oversized persisted labels can't crash layout.
    const safeContent = clampTextLength(label.content ?? '', MAX_LABEL_CONTENT_LENGTH);

    const activeFontSize = label.fontSize ?? DEFAULT_LABEL_FONT;

    // ── Edit mode ──────────────────────────────────────────────────────────────

    const enterEditMode = useCallback(() => {
      flushSync(() => setIsEditing(true));
      onEditingChange?.(label.id);
      const el = editRef.current;
      if (!el) return;
      el.innerText = safeContent;
      el.focus();
      const range = document.createRange();
      const sel   = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      onHasTextChange?.(label.id, el.innerText.trim().length > 0);
    }, [onEditingChange, onHasTextChange, safeContent, label.id]);

    const handleDoubleClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      if (occupiedByName) return;
      enterEditMode();
    }, [enterEditMode, occupiedByName]);

    const handleBeforeInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
      const el = editRef.current;
      if (!el) return;
      handleContentEditableBeforeInput(e.nativeEvent as InputEvent, el, MAX_LABEL_CONTENT_LENGTH);
    }, []);

    const handleInput = useCallback(() => {
      const el = editRef.current;
      if (!el) return;
      truncateContentEditableIfNeeded(el, MAX_LABEL_CONTENT_LENGTH);
      // contenteditable reports "\n" for an empty editor — trim before checking
      onHasTextChange?.(label.id, (el.innerText ?? '').trim().length > 0);
    }, [onHasTextChange, label.id]);

    const handleBlur = useCallback(() => {
      if (suppressPersistRef.current) {
        suppressPersistRef.current = false;
        setIsEditing(false);
        onEditingChange?.(null);
        return;
      }
      setIsEditing(false);
      onEditingChange?.(null);
      const text = clampTextLength((editRef.current?.innerText ?? '').trim(), MAX_LABEL_CONTENT_LENGTH);
      // Titles can't be blank — an empty label is discarded rather than persisted.
      if (!text) {
        onDelete(label.id);
        return;
      }
      onChange({ id: label.id, content: text });
    }, [onChange, onDelete, onEditingChange, label.id]);

    // Externally triggered edit mode (from FAB action or keyboard Enter).
    // useLayoutEffect (not useEffect) so contentEditable flips on before the browser's
    // first paint — otherwise the fit-content box briefly renders at its collapsed,
    // non-editable height and the color strip (measured from that box) visibly jumps
    // down once the caret's line-box height kicks in a frame later.
    useLayoutEffect(() => {
      if (!shouldStartEdit) return;
      setIsEditing(true);
      onEditingChange?.(label.id);
      setTimeout(() => {
        const el = editRef.current;
        if (!el) return;
        el.innerText = safeContent;
        el.focus();
        const range = document.createRange();
        const sel   = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
        onHasTextChange?.(label.id, el.innerText.trim().length > 0);
      }, 0);
    }, [shouldStartEdit]); // intentionally omits deps — runs only when shouldStartEdit flips

    // ── Drag ──────────────────────────────────────────────────────────────────

    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        if (isEditing) return;
        if (isPanMode) return;
        if (occupiedByName) return;
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
        // In multi-select mode keep the full selection intact; single click will collapse on pointer-up
        if (!isMultiSelected) onSelect(label.id);
        onDraggingChange?.(label.id);
        dragStartPos.current = { mx: e.clientX, my: e.clientY, bx: label.x, by: label.y };
        livePosRef.current = null;
        setIsDragging(true);
        setIsMoving(false);
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      },
      [isEditing, isPanMode, isMultiSelected, occupiedByName, onDraggingChange, onSelect, label.id, label.x, label.y],
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!dragStartPos.current || !isDragging) return;
        const dx = (e.clientX - dragStartPos.current.mx) / scale;
        const dy = (e.clientY - dragStartPos.current.my) / scale;
        const sdx = e.clientX - dragStartPos.current.mx;
        const sdy = e.clientY - dragStartPos.current.my;
        const pastThreshold = isMoving || Math.hypot(sdx, sdy) > POINTER_DRAG_THRESHOLD_PX;
        if (!pastThreshold) return;
        // Live position is DOM-imperative (see liveDragPosition.ts). Do not wait
        // for parent onChange to drive React x/y — that re-renders the whole board.
        // Apply before setIsMoving so the lift-effect render cannot snap back to sx.
        const x = dragStartPos.current.bx + dx;
        const y = dragStartPos.current.by + dy;
        livePosRef.current = { x, y };
        applyLiveItemPosition(e.currentTarget as HTMLElement, x, y);
        if (!isMoving) setIsMoving(true);
        if (isMultiSelected && onMultiDragMove) {
          onMultiDragMove(label.id, dx, dy);
        } else {
          onChange({ id: label.id, x, y });
        }
      },
      [isDragging, isMoving, isMultiSelected, onChange, onMultiDragMove, scale, label.id],
    );

    const handlePointerUp = useCallback(
      (e: React.PointerEvent) => {
        if (!dragStartPos.current || !isDragging) return;
        const startPos = dragStartPos.current;
        const dx       = (e.clientX - startPos.mx) / scale;
        const dy       = (e.clientY - startPos.my) / scale;
        const wasClick = Math.hypot(e.clientX - startPos.mx, e.clientY - startPos.my) <= POINTER_DRAG_THRESHOLD_PX;
        dragStartPos.current = null;
        livePosRef.current = null;
        setIsDragging(false);
        setIsMoving(false);
        onDraggingChange?.(null);
        if (isMultiSelected) {
          if (wasClick) {
            // Collapse the multi-selection to just this label
            onSelect(label.id);
          } else {
            onMultiDragEnd?.(label.id, dx, dy);
          }
          return;
        }
        if (!wasClick) {
          onDragEnd(label.id, startPos.bx + dx, startPos.by + dy);
          return;
        }
        onDragEnd(label.id, startPos.bx + dx, startPos.by + dy);
        if (!label.content.trim()) enterEditMode();
      },
      [enterEditMode, isDragging, isMultiSelected, onDragEnd, onDraggingChange, onMultiDragEnd, onSelect, scale, label.content, label.id],
    );

    // Re-apply live left/top after isMoving flips (Emotion sx would otherwise
    // snap back to the start x/y from props). Clear after drop so sx owns position.
    useLayoutEffect(() => {
      if (isDragging && livePosRef.current) {
        applyLiveItemPosition(nodeRef.current, livePosRef.current.x, livePosRef.current.y);
        return;
      }
      clearLiveItemPosition(nodeRef.current);
    }, [isDragging, isMoving, label.x, label.y]);

    useEffect(() => {
      const epochBumped = occupancyAbortEpoch > 0
        && occupancyAbortEpoch !== occupancyAbortEpochRef.current;
      occupancyAbortEpochRef.current = occupancyAbortEpoch;
      if (!occupiedByName && !epochBumped) return;
      if (isEditing) {
        suppressPersistRef.current = true;
        setIsEditing(false);
        onEditingChange?.(null);
      }
      if (!isDragging && !isMoving) return;
      dragStartPos.current = null;
      livePosRef.current = null;
      clearLiveItemPosition(nodeRef.current);
      setIsDragging(false);
      setIsMoving(false);
      onDraggingChange?.(null);
    }, [occupiedByName, occupancyAbortEpoch, isEditing, isDragging, isMoving, onDraggingChange, onEditingChange]);

    // ── Keyboard delete ────────────────────────────────────────────────────────

    useEffect(() => {
      if (!isSelected || isEditing) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Delete' || e.key === 'Backspace') onDelete(label.id);
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, [isSelected, isEditing, onDelete, label.id]);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
      <Box
        ref={setRootRef}
        className="text-label"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onLostPointerCapture={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        sx={{
          position:    'absolute',
          left:         label.x,
          top:          label.y,
          // Width follows the text — the outline hugs the content
          width:       'fit-content',
          whiteSpace:  'nowrap',
          background:  'transparent',
          borderRadius: '4px',
          outline:     isSelected ? `2px solid ${selectionColor}` : '2px solid transparent',
          outlineOffset: `${LABEL_OUTLINE_OFFSET_PX}px`,
          cursor:      isMoving ? 'grabbing' : 'grab',
          userSelect:  'none',
          display:     'inline-flex',
          alignItems:  'center',
          boxSizing:   'border-box',
          padding:     `${LABEL_PADDING_Y_PX}px ${LABEL_PADDING_X_PX}px`,
          transform:   isMoving ? 'rotate(1deg) scale(1.02)' : 'none',
          transition:  isMoving ? 'none' : 'outline 0.15s, transform 0.1s',
          zIndex:      displayZIndex(label, isMoving),
          touchAction: 'none',
        }}
      >
        <Box
          ref={editRef}
          contentEditable={isEditing}
          suppressContentEditableWarning
          onBeforeInput={handleBeforeInput}
          onInput={handleInput}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.stopPropagation(); editRef.current?.blur(); }
          }}
          sx={{
            fontSize:    `${activeFontSize}px`,
            fontFamily:  TEXT_FONT_FAMILY,
            fontWeight:  700,
            color:       resolvedColor,
            lineHeight:  TEXT_LINE_HEIGHT,
            whiteSpace:  'nowrap',
            outline:     'none',
            cursor:      'inherit',
            pointerEvents: isEditing ? 'auto' : 'none',
            // Prevent contenteditable from adding a trailing newline that widens the box
            minWidth:    `${LABEL_INNER_MIN_WIDTH_PX}px`,
            // An empty div (no text, no <br>) collapses to zero height until the browser
            // lazily inserts a caret line-box on focus — which happens a frame after the
            // first paint and made the color strip below visibly jump down. Reserving one
            // line's height up front keeps the box (and anything measured off it) stable
            // from the very first render, whether editing or not, empty or not.
            minHeight:   `${activeFontSize * TEXT_LINE_HEIGHT}px`,
          }}
        >
          {!isEditing && safeContent}
        </Box>

        {showResizeHandles && onResizePointerDown && onResizePointerMove && onResizePointerUp && (
          <ResizeCornerHandles
            outset={LABEL_OUTLINE_OFFSET_PX}
            scale={scale}
            selectionColor={selectionColor}
            onCornerPointerDown={onResizePointerDown}
            onCornerPointerMove={onResizePointerMove}
            onCornerPointerUp={onResizePointerUp}
            onCornerPointerCancel={onResizePointerCancel}
            onCornerLostPointerCapture={onResizeLostPointerCapture}
          />
        )}
        {occupiedByName && <ItemOccupancyOverlay />}
      </Box>
    );
  },
);

export default TextLabel;
